import { distanceSquared, opposite, sameTile, step } from "./direction";
import { EPSILON, hop, onCentre, tileOf } from "./motion";
import type { Maze } from "./maze";
import { DIRECTIONS, type Direction, type GhostMode, type GhostName, type GhostState, type Tile } from "./types";

/**
 * AI Slop movement, ported from the original Pac-Man ghost algorithm.
 *
 * The three rules that actually produce the behaviour (see docs/GHOST_AI.md):
 *
 *  1. Every ghost always has a single **target tile**. It never pathfinds; at
 *     each tile it looks one tile ahead down each legal direction and takes
 *     whichever candidate is closest to the target as the crow flies.
 *  2. It may not reverse. Only a forced mode change (scatter <-> chase, or a
 *     5h Quota Reset) flips a ghost around.
 *  3. Ties break in a fixed order: up, then left, then down, then right.
 *
 * What differs per ghost is only rule 1's target, which is why four ghosts
 * running one algorithm feel like four different personalities.
 */

/** Decision-order for ties. Matches the arcade exactly; do not re-order. */
const TIE_BREAK_ORDER: readonly Direction[] = ["up", "left", "down", "right"];

export interface TargetInputs {
  readonly playerTile: Tile;
  readonly playerDir: Direction;
  readonly blinkyTile: Tile;
}

/**
 * Four tiles ahead of the player, reproducing the arcade's overflow bug.
 *
 * On the real hardware the "ahead" offset was added to both axes when the
 * player faced up, because the routine reused one add for the diagonal case.
 * Pinky and Inky both go through it, and players exploit it, so it is kept.
 */
export const aheadOfPlayer = (tile: Tile, dir: Direction, n: number): Tile => {
  const ahead = step(tile, dir, n);
  return dir === "up" ? { x: ahead.x - n, y: ahead.y } : ahead;
};

/** The chase-mode target tile for one ghost. */
export const chaseTarget = (name: GhostName, self: Tile, inputs: TargetInputs, scatter: Tile): Tile => {
  const { playerTile, playerDir, blinkyTile } = inputs;
  switch (name) {
    // Blinky ("Shadow") -- the player's own tile. Relentless.
    case "blinky":
      return playerTile;
    // Pinky ("Speedy") -- four tiles ahead, so she cuts the player off.
    case "pinky":
      return aheadOfPlayer(playerTile, playerDir, 4);
    // Inky ("Bashful") -- the vector from Blinky to two-ahead of the player,
    // doubled. He is only dangerous when Blinky is already closing in.
    case "inky": {
      const pivot = aheadOfPlayer(playerTile, playerDir, 2);
      return { x: pivot.x * 2 - blinkyTile.x, y: pivot.y * 2 - blinkyTile.y };
    }
    // Clyde ("Pokey") -- the player, until he gets within eight tiles, at
    // which point he loses his nerve and runs for his scatter corner.
    case "clyde":
      return distanceSquared(self, playerTile) >= 64 ? playerTile : scatter;
  }
};

/**
 * Pick a direction at `tile`, given where the ghost is heading now.
 *
 * `frightenedPick` supplies the pseudo-random choice used while fleeing; the
 * arcade uses a hardware counter, we take a seeded generator so replays and
 * tests are deterministic. It is handed the already-filtered legal options.
 */
export const chooseDirection = (
  maze: Maze,
  tile: Tile,
  facing: Direction,
  target: Tile,
  opts: { readonly canUseDen: boolean; readonly ignoreNoUpTurn: boolean; readonly frightenedPick?: (options: readonly Direction[]) => Direction },
): Direction => {
  const back = opposite(facing);
  let options = TIE_BREAK_ORDER.filter((dir) => {
    if (dir === back) return false;
    if (!maze.isOpen(step(tile, dir), opts.canUseDen)) return false;
    // Rule 3 of the arcade's movement code: four junctions ban the up turn.
    if (dir === "up" && !opts.ignoreNoUpTurn && maze.isNoUpTurnTile(tile)) return false;
    return true;
  });

  // A dead end is the one place reversing is legal without a mode change.
  if (options.length === 0) return back;

  if (opts.frightenedPick !== undefined) return opts.frightenedPick(options);

  let best = options[0]!;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const dir of options) {
    // TIE_BREAK_ORDER is the iteration order, and the comparison is strict, so
    // the first direction to reach the minimum wins: up > left > down > right.
    const dist = distanceSquared(maze.normalise(step(tile, dir)), target);
    if (dist < bestDist) {
      bestDist = dist;
      best = dir;
    }
  }
  options = [];
  return best;
};

/**
 * The level-1 scatter/chase schedule, in seconds.
 *
 * Seven wave boundaries then an indefinite chase; each boundary forces every
 * hunting ghost to reverse, which is the player's cue that the mode flipped.
 */
export const WAVE_SCHEDULE: readonly { readonly mode: GhostMode; readonly seconds: number }[] = [
  { mode: "scatter", seconds: 7 },
  { mode: "chase", seconds: 20 },
  { mode: "scatter", seconds: 7 },
  { mode: "chase", seconds: 20 },
  { mode: "scatter", seconds: 5 },
  { mode: "chase", seconds: 20 },
  { mode: "scatter", seconds: 5 },
  { mode: "chase", seconds: Number.POSITIVE_INFINITY },
];

/** Which wave is active `elapsed` seconds into a life. */
export const waveAt = (elapsed: number): { mode: GhostMode; index: number } => {
  let remaining = elapsed;
  for (let i = 0; i < WAVE_SCHEDULE.length; i += 1) {
    const wave = WAVE_SCHEDULE[i]!;
    if (remaining < wave.seconds) return { mode: wave.mode, index: i };
    remaining -= wave.seconds;
  }
  const last = WAVE_SCHEDULE[WAVE_SCHEDULE.length - 1]!;
  return { mode: last.mode, index: WAVE_SCHEDULE.length - 1 };
};

export interface GhostConfig {
  readonly name: GhostName;
  /** Story Points the player must eat before this ghost leaves the den. */
  readonly dotLimit: number;
}

export const GHOST_ORDER: readonly GhostConfig[] = [
  { name: "blinky", dotLimit: 0 },
  { name: "pinky", dotLimit: 0 },
  { name: "inky", dotLimit: 30 },
  { name: "clyde", dotLimit: 60 },
];

export interface GhostSnapshot {
  readonly name: GhostName;
  readonly tile: Tile;
  readonly x: number;
  readonly y: number;
  readonly dir: Direction;
  readonly state: GhostState;
  readonly frightened: boolean;
}

/** A deterministic 32-bit generator, so a seed replays a whole game exactly. */
export class Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0 || 0x9e3779b9;
  }

  next(): number {
    // xorshift32
    let x = this.state;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.state = x >>> 0;
    return this.state / 0x100000000;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error("cannot pick from an empty list");
    return items[Math.floor(this.next() * items.length) % items.length]!;
  }
}

export class Ghost {
  readonly name: GhostName;
  readonly dotLimit: number;
  x: number;
  y: number;
  dir: Direction;
  state: GhostState;
  frightened = false;
  /** Set by a mode change; consumed at the next tile centre. */
  pendingReverse = false;

  readonly maze: Maze;
  private readonly spawn: Tile;

  constructor(maze: Maze, config: GhostConfig, spawn: Tile, state: GhostState) {
    this.maze = maze;
    this.name = config.name;
    this.dotLimit = config.dotLimit;
    this.spawn = spawn;
    this.x = spawn.x;
    this.y = spawn.y;
    this.dir = state === "house" ? "up" : "left";
    this.state = state;
  }

  get tile(): Tile {
    return tileOf(this);
  }

  reset(state: GhostState): void {
    this.x = this.spawn.x;
    this.y = this.spawn.y;
    this.dir = state === "house" ? "up" : "left";
    this.state = state;
    this.frightened = false;
    this.pendingReverse = false;
  }

  snapshot(): GhostSnapshot {
    return {
      name: this.name,
      tile: this.tile,
      x: this.x,
      y: this.y,
      dir: this.dir,
      state: this.state,
      frightened: this.frightened,
    };
  }

  /** Where this ghost is currently heading. */
  targetTile(mode: GhostMode, inputs: TargetInputs): Tile {
    const scatter = this.maze.scatterTargets[this.name];
    if (this.state === "eaten") return this.maze.spawns[this.name];
    if (this.state === "house" || this.state === "leaving") return this.maze.spawns.denExit;
    if (this.frightened) return scatter;
    if (mode === "scatter") return scatter;
    return chaseTarget(this.name, this.tile, inputs, scatter);
  }

  /**
   * Advance by `distance` tiles, re-deciding at each tile centre.
   *
   * Movement is continuous but decisions are tile-quantised, exactly as the
   * arcade does it: a ghost commits to a direction on entering a tile and
   * cannot change its mind until it reaches the next one.
   */
  advance(distance: number, mode: GhostMode, inputs: TargetInputs, rng: Rng): void {
    let remaining = distance;
    let guard = 0;
    while (remaining > EPSILON) {
      if (guard++ > 512) throw new Error(`${this.name} failed to converge while moving`);

      if (onCentre(this)) {
        this.decide(mode, inputs, rng);
        const ahead = this.maze.normalise(step(this.tile, this.dir));
        if (!this.canEnter(ahead)) return; // boxed in; wait here
      }

      const { moved } = hop(this, remaining);
      remaining -= moved;
    }
  }

  /**
   * The den gate is one-way, and this is what enforces it.
   *
   * Only a ghost on its way out or returning as eyes may pass the gate or
   * stand on den floor. A hunting ghost sees both as hull -- without this it
   * will happily take the gate as a shortcut towards a player standing below
   * the den and then rattle around inside for the rest of the level.
   */
  get canUseDen(): boolean {
    return this.state !== "hunting";
  }

  private canEnter(tile: Tile): boolean {
    return this.maze.isOpen(tile, this.canUseDen);
  }

  private decide(mode: GhostMode, inputs: TargetInputs, rng: Rng): void {
    const tile = this.tile;

    if (this.state === "eaten" && sameTile(tile, this.maze.spawns[this.name])) {
      this.state = "house";
      this.frightened = false;
    }
    if (this.state === "leaving" && sameTile(tile, this.maze.spawns.denExit)) {
      this.state = "hunting";
      this.dir = "left";
      return;
    }

    if (this.pendingReverse) {
      this.pendingReverse = false;
      const back = opposite(this.dir);
      if (this.canEnter(this.maze.normalise(step(tile, back)))) {
        this.dir = back;
        return;
      }
    }

    // Inside the den, or heading home: steer by search, since the den's walls
    // make straight-line targeting dead-end.
    if (this.state === "house") {
      this.bobInDen();
      return;
    }
    if (this.state === "leaving" || this.state === "eaten") {
      const target = this.targetTile(mode, inputs);
      const towards = this.maze.stepTowards(tile, target, true);
      if (towards !== null) this.dir = towards;
      return;
    }

    const options = this.maze.openDirections(tile, this.canUseDen).filter((d) => d !== opposite(this.dir));
    // Straight corridor: keep going. Only junctions and corners re-target.
    if (options.length === 1 && options[0] === this.dir) return;

    this.dir = chooseDirection(this.maze, tile, this.dir, this.targetTile(mode, inputs), {
      canUseDen: this.canUseDen,
      // A ghost returning to the den ignores the up-turn ban, as in the arcade.
      ignoreNoUpTurn: this.state !== "hunting",
      ...(this.frightened ? { frightenedPick: (o: readonly Direction[]) => rng.pick(o) } : {}),
    });
  }

  /** Idle hop inside the den while waiting for the dot counter to release. */
  private bobInDen(): void {
    const up = step(this.tile, "up");
    const down = step(this.tile, "down");
    if (this.dir === "up" && this.maze.kindAt(up) !== "den") this.dir = "down";
    else if (this.dir === "down" && this.maze.kindAt(down) !== "den") this.dir = "up";
    else if (this.dir !== "up" && this.dir !== "down") this.dir = "up";
  }

  /** Called when the dot counter or timer releases this ghost. */
  release(): void {
    if (this.state === "house") {
      this.state = "leaving";
      this.frightened = false;
    }
  }
}

export const allDirections = DIRECTIONS;
